/**
 * UUID Encryption/Decryption Utility
 *
 * Implements AES-256-GCM encryption compatible with backend Go implementation.
 * Uses Web Crypto API (available in modern browsers) for GCM mode support.
 */

/**
 * Get encryption key from environment or use default
 * Key will be hashed to 32 bytes (SHA-256) for AES-256
 */
function getEncryptionKey() {
  // Use environment variable if available
  // Check both process.env (Node.js) and window (browser)
  const keyFromEnv =
    (typeof process !== 'undefined' && process.env?.ID_ENCRYPTION_KEY) ||
    (typeof window !== 'undefined' && window?.process?.env?.ID_ENCRYPTION_KEY) ||
    null

  if (keyFromEnv) {
    return keyFromEnv
  }

  // Default key - MUST match backend default key
  // In production, this should be set via environment variable
  return 'default-encryption-key-32-bytes-long!!'
}

/**
 * Hash key to 32 bytes using SHA-256 (compatible with backend)
 * @param {string} key - The key string to hash
 * @returns {Promise<Uint8Array>} - 32-byte key
 */
async function hashKey(key) {
  const encoder = new TextEncoder()
  const data = encoder.encode(key)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  return new Uint8Array(hashBuffer)
}

/**
 * Generate encryption key for Web Crypto API
 * @returns {Promise<CryptoKey>} - CryptoKey for AES-GCM
 */
async function getCryptoKey() {
  const keyString = getEncryptionKey()
  const keyBytes = await hashKey(keyString)

  return crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ])
}

/**
 * Check if Web Crypto API is available
 * Only available in browser context, not in SSR
 * @returns {boolean}
 */
function isWebCryptoAvailable() {
  // Only available in browser (client-side)
  if (typeof window === 'undefined') {
    return false
  }

  return (
    typeof crypto !== 'undefined' && crypto.subtle && typeof crypto.subtle.encrypt === 'function'
  )
}

/**
 * Encrypts a UUID string and returns a base64-encoded encrypted string
 * @param {string} uuidStr - The UUID string to encrypt
 * @returns {Promise<string>} - Encrypted base64 string
 */
export async function encryptUUID(uuidStr) {
  if (!uuidStr || uuidStr === '') {
    return uuidStr
  }

  // Validate UUID format first
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (!uuidRegex.test(uuidStr)) {
    // If it's not a valid UUID, return as is
    return uuidStr
  }

  // Check if Web Crypto API is available
  if (!isWebCryptoAvailable()) {
    console.warn('Web Crypto API not available, returning UUID as-is')
    return uuidStr
  }

  try {
    const key = await getCryptoKey()
    const encoder = new TextEncoder()
    const data = encoder.encode(uuidStr)

    // Generate random IV (nonce) - 12 bytes for GCM
    const iv = crypto.getRandomValues(new Uint8Array(12))

    // Encrypt using AES-GCM
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      data
    )

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength)
    combined.set(iv, 0)
    combined.set(new Uint8Array(encryptedBuffer), iv.length)

    // Encode to base64 URL-safe without padding (compatible with backend)
    const base64 = btoa(String.fromCharCode(...combined))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')

    return base64
  } catch (error) {
    console.error('Error encrypting UUID:', error)
    // Return original UUID on error
    return uuidStr
  }
}

/**
 * Decrypts a base64-encoded encrypted string and returns the original UUID
 * @param {string} encryptedStr - The encrypted string to decrypt
 * @returns {Promise<string>} - Decrypted UUID string
 */
export async function decryptUUID(encryptedStr) {
  if (!encryptedStr || encryptedStr === '') {
    return encryptedStr
  }

  // Check if Web Crypto API is available
  if (!isWebCryptoAvailable()) {
    // Try to parse as UUID (might be plain UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (uuidRegex.test(encryptedStr)) {
      return encryptedStr
    }
    console.warn('Web Crypto API not available, returning string as-is')
    return encryptedStr
  }

  try {
    // Add padding if needed for base64 decode
    let padded = encryptedStr
    while (padded.length % 4 !== 0) {
      padded += '='
    }

    // Convert from URL-safe base64 to standard base64
    const base64Str = padded.replace(/-/g, '+').replace(/_/g, '/')

    // Decode base64
    const binaryString = atob(base64Str)
    const combined = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      combined[i] = binaryString.charCodeAt(i)
    }

    // Extract IV (first 12 bytes) and ciphertext
    const iv = combined.slice(0, 12)
    const ciphertext = combined.slice(12)

    const key = await getCryptoKey()

    // Decrypt using AES-GCM
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      ciphertext
    )

    // Convert decrypted data to string
    const decoder = new TextDecoder()
    const uuidStr = decoder.decode(decryptedBuffer)

    // Validate decrypted UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(uuidStr)) {
      throw new Error('Decrypted value is not a valid UUID')
    }

    return uuidStr
  } catch (error) {
    // If decryption fails, try to parse as UUID (might be plain UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (uuidRegex.test(encryptedStr)) {
      return encryptedStr
    }

    // If not a UUID either, log warning and return original
    console.warn('Error decrypting UUID, returning original:', error)
    return encryptedStr
  }
}

/**
 * Synchronous version of encryptUUID (wraps async version)
 * Note: This will return a Promise, use in async contexts
 */
export function encryptUUIDSync(uuidStr) {
  return encryptUUID(uuidStr)
}

/**
 * Synchronous version of decryptUUID (wraps async version)
 * Note: This will return a Promise, use in async contexts
 */
export function decryptUUIDSync(encryptedStr) {
  return decryptUUID(encryptedStr)
}

/**
 * Checks if a string is an encrypted ID (not a plain UUID)
 * @param {string} idStr - The ID string to check
 * @returns {boolean} - True if encrypted, false if plain UUID
 */
export function isEncryptedID(idStr) {
  if (!idStr || idStr === '') {
    return false
  }

  // Try to parse as UUID first
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (uuidRegex.test(idStr)) {
    return false // It's a valid UUID, so it's not encrypted
  }

  // If not a UUID, assume it's encrypted (base64-like string)
  return true
}

/**
 * Recursively encrypts all UUID fields in an object/array
 * @param {any} data - The data to encrypt ID fields in
 * @returns {Promise<any>} - Data with encrypted ID fields
 */
export async function encryptIDFields(data) {
  if (data === null || data === undefined) {
    return data
  }

  if (Array.isArray(data)) {
    const encrypted = await Promise.all(data.map((item) => encryptIDFields(item)))
    return encrypted
  }

  if (typeof data === 'object') {
    const result = {}
    const promises = []

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const lowerKey = key.toLowerCase()
        // Check if key ends with _id or is exactly "id"
        if (lowerKey === 'id' || lowerKey.endsWith('_id')) {
          if (typeof data[key] === 'string' && data[key] !== '') {
            promises.push(
              encryptUUID(data[key]).then((encrypted) => {
                result[key] = encrypted
              })
            )
          } else {
            promises.push(
              encryptIDFields(data[key]).then((encrypted) => {
                result[key] = encrypted
              })
            )
          }
        } else {
          promises.push(
            encryptIDFields(data[key]).then((encrypted) => {
              result[key] = encrypted
            })
          )
        }
      }
    }

    await Promise.all(promises)
    return result
  }

  return data
}

/**
 * Recursively decrypts all encrypted ID fields in an object/array
 * @param {any} data - The data to decrypt ID fields in
 * @returns {Promise<any>} - Data with decrypted ID fields
 */
export async function decryptIDFields(data) {
  if (data === null || data === undefined) {
    return data
  }

  if (Array.isArray(data)) {
    const decrypted = await Promise.all(data.map((item) => decryptIDFields(item)))
    return decrypted
  }

  if (typeof data === 'object') {
    const result = {}
    const promises = []

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const lowerKey = key.toLowerCase()
        // Check if key ends with _id or is exactly "id"
        if (lowerKey === 'id' || lowerKey.endsWith('_id')) {
          if (typeof data[key] === 'string' && data[key] !== '') {
            promises.push(
              decryptUUID(data[key]).then((decrypted) => {
                result[key] = decrypted
              })
            )
          } else {
            promises.push(
              decryptIDFields(data[key]).then((decrypted) => {
                result[key] = decrypted
              })
            )
          }
        } else {
          promises.push(
            decryptIDFields(data[key]).then((decrypted) => {
              result[key] = decrypted
            })
          )
        }
      }
    }

    await Promise.all(promises)
    return result
  }

  return data
}
